import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { NotificationManager } from '@atlas/core';
import { Icon, Label } from '@atlas/ui';

import { onSetLostLimitsSubmit } from 'common/Form/formConfig/responsibleGaming';
import FormCom from 'common/Form/FormMobile';
import useTranslate from 'common/I18n/Interpreter';
import { getLimitWarning } from 'common/utils/utils';
import LimitInput from 'src/components/ClientProfile/ResponsibleGaming/SelfRegulation/Limits/LimitInput';
import useFormatter from 'src/hooks/useFormatter';

const MIN_VALUE = 100; // 1.00
const LimitForm = ({
  checkCommonSpentLimit,
  onClosePopup,
  regulationLimit,
  setRegulationLimit,
  type,
}) => {
  const t = useTranslate();
  const { currencyFormatter } = useFormatter();
  const formRef = useRef(null);
  const [disabledSaveButtons, setDisabledSaveButtons] = useState({});
  const formFields = useMemo(() => {
    const fields = [];
    regulationLimit.forEach(({ defaultValue, error = false, future, type: name, value }) => {
      fields.push({
        component: props => (
          <LimitInput
            {...props}
            onChange={(value, touched) => {
              // removes leading zeros and strictly enforces MIN_VALUE if the input is empty or < 1
              const safeValue = value === null || value === undefined ? '' : String(value);
              const cleanValue = safeValue.replace(/^0+(?=\d)/, '');
              const numValue = Number(cleanValue);
              if (cleanValue === '' || Number.isNaN(numValue)) {
                setDisabledSaveButtons(prevState => ({
                  ...prevState,
                  [name]: true,
                }));
              } else {
                setDisabledSaveButtons(prevState => ({
                  ...prevState,
                  [name]: false,
                }));
              }

              const finalValue = numValue < 1 ? MIN_VALUE : cleanValue;

              props.onChange(finalValue, touched);
            }}
            required
          />
        ),
        error,
        errorMessage: 'limit_error_common_message',
        formRef,
        name,
        rules: [],
        value: value ?? defaultValue ?? MIN_VALUE,
        warning: getLimitWarning(future, name),
      });
    });

    return {
      fields,
      name: 'selfRegulationLimits',
    };
  }, [regulationLimit]);

  const action = useCallback(
    async (formData, actions) => {
      const isError = checkCommonSpentLimit({
        [type]: formData,
      });
      if (isError) return;
      return onSetLostLimitsSubmit(formData, actions, type);
    },
    [checkCommonSpentLimit, type],
  );

  const submitCallback = useCallback(
    data => {
      if (data) {
        const notificationText = data.reduce((acc, text) => {
          const periodLimit = regulationLimit.find(period => period.type === text.type);
          if (text.value && (text.value < periodLimit.value || !periodLimit.value)) {
            const processedValue = currencyFormatter.format(text.value);
            return `${acc}, ${t(`responsible_gaming_${text.type}`)} - ${processedValue}`;
          }
          return acc;
        }, '');

        if (notificationText.length) {
          NotificationManager.notify({
            data: {
              type: t(`${type.toLowerCase()}_limits`),
              value: `${notificationText.slice(2)} ${t('limits_was_activated')}`,
            },
            message: 'limits_were_edited',
            status: 'success',
            type: 'notification',
          });
        }
        setRegulationLimit(data, type);
        onClosePopup();
      }
    },
    [setRegulationLimit, type, onClosePopup, regulationLimit, currencyFormatter, t],
  );

  const isSaveButtonDisabled = Object.values(disabledSaveButtons).some(v => v);

  const zeroLimitBlock = useMemo(
    () => (
      <div className="limit__zero-block limit__zero-block--error">
        <Icon height={24} name="self-regulation-info" />
        <Label message="common_limit_please_fill_message" />
      </div>
    ),
    [],
  );

  return (
    <div className="limit-form">
      <Label className="limit-form__description" message="common_spent_limit_popup_description" />
      <FormCom
        action={action}
        buttonText="save"
        centerContent={isSaveButtonDisabled && zeroLimitBlock}
        className="self-regulation__form"
        disableButton={isSaveButtonDisabled}
        form={formFields}
        onSubmit={submitCallback}
        ref={formRef}
      />
    </div>
  );
};

LimitForm.propTypes = {
  chackLimitHandler: PropTypes.func,
  label: PropTypes.string,
  regulationLimit: PropTypes.array,
  setRegulationLimit: PropTypes.func,
  type: PropTypes.string,
};

export default LimitForm;
